import { DOWN_ARROW } from "@angular/cdk/keycodes";
import { CdkConnectedOverlay } from "@angular/cdk/overlay";
import { NgForOf, NgTemplateOutlet, UpperCasePipe } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { MatListOption, MatSelectionListChange } from "@angular/material/list";
import { Router } from "@angular/router";

import { OverlayModule } from "@angular/cdk/overlay";
import { NgIf } from "@angular/common";
import { MatButton } from "@angular/material/button";
import { MatListModule } from "@angular/material/list";
import { TranslateModule } from "@ngx-translate/core";
import { AuthService } from "app/core/auth/service/auth.service";
import { AccountService } from "app/core/services/account.service";
import { CacheService } from "app/core/services/cache.service";
import { TrunksetDataService } from "app/features/nconnect-voice/services/api/trunkset-data/trunkset-data.service";
import { LoadingAreaIndicatorComponent } from "app/shared/components/loading-area-indicator/loading-area-indicator.component";
import { SvgComponent } from "app/shared/components/svg/svg-icon/svg-icon.component";
import { EllipsisTitleDirective } from "app/shared/directives/ellipsis-title.directive";
import { Account } from "app/shared/models/account";
import { UserModule } from "app/shared/models/modules-names";
import { InfiniteScrollDirective } from "ngx-infinite-scroll";
import { Subject, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  startWith,
  switchMap,
  take,
  takeUntil,
} from "rxjs/operators";
@Component({
  selector: "account-select",
  templateUrl: "./account-select.component.html",
  styleUrls: ["./account-select.component.scss"],
  imports: [
    MatButton,
    EllipsisTitleDirective,
    NgTemplateOutlet,
    SvgComponent,
    OverlayModule,
    NgIf,
    TranslateModule,
    UpperCasePipe,
    MatListModule,
    NgForOf,
    InfiniteScrollDirective,
    LoadingAreaIndicatorComponent,
  ],
})
export class AccountSelectComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  public static RECENT_ACCOUNTS: string = "RECENT_ACCOUNTS";

  private searchInputValue$: Subject<string> = new Subject<string>();
  private accountsRequest?: Subscription;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private userId!: string;

  @Input() public e2e: string;

  public accounts: Account[] = [];
  public recentAccounts: any[] =
    AccountSelectComponent.storedRecentAccounts || [];
  public isListOpen: boolean = false;
  public selectedAccount: Account | null = null;
  public searchInputValue: string = "";
  public hasMoreData: boolean = false;
  public isLoading: boolean = false;
  public page: number = 1;
  public pageSize: number = 25;
  public inactiveAccountStatus: string = "blocked";

  @ViewChildren(MatListOption) public matListOptions:
    | QueryList<MatListOption>
    | undefined;
  @ViewChild("overlay", { static: false }) public overlay:
    | CdkConnectedOverlay
    | undefined;
  @ViewChild("searchInput", { static: false }) public searchInput:
    | ElementRef
    | undefined;

  constructor(
    private accountService: AccountService,
    private router: Router,
    private trunksetDataService: TrunksetDataService,
    private authService: AuthService,
  ) {}

  public ngOnInit(): void {
    // TODO - https://jira.nfon.net/browse/NFON-23996
    this.searchInputValue$
      .pipe(
        startWith(this.searchInputValue),
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(this.initSearch.bind(this));

    this.accountService.selectedAccount$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((account: Account | null) => {
        this.selectedAccount = account;

        if (account && this.userId) {
          this.accountService.storeLastUsedAccount(account, this.userId);
        }
      });

    // This will run whenever the user changes the customer
    this.accountService.selectedAccountModules$
      .pipe(
        filter((modules: UserModule[]) =>
          modules.includes(UserModule.NConnectVoice),
        ),
        switchMap(() => this.trunksetDataService.getTrunksets()),
      )
      .toPromise();

    this.getLoggedUser();
  }

  public ngAfterViewInit(): void {
    this.overlay!.positionChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      () => {
        this.searchInput!.nativeElement.focus();
      },
    );
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public initSearch(value: string): void {
    this.searchInputValue = value;
    this.page = 1;
    this.accounts = [];
    this.loadAccounts();
  }

  public search(event: any): void {
    this.searchInputValue$.next(event.target.value);
  }

  public onSelectionChange(event: MatSelectionListChange): void {
    const account: any = event.options[0].value;

    this.selectedAccount = account;
    this.accountService.selectedAccount = account;
    this.addToRecents(account);

    if (!this.router.routerState.snapshot.url.match(/^\/shortcuts/)) {
      this.router.navigate([`/nconnect-voice/shortcuts`]);
    }
  }

  public onKeyDown(event: KeyboardEvent): void {
    const keyCode: any = event.keyCode;

    if (keyCode === DOWN_ARROW) {
      event.preventDefault();
      if (this.matListOptions && this.matListOptions.length) {
        this.matListOptions.first.focus();
      }
    }
  }

  public closeList(): void {
    this.isListOpen = false;
    this.searchInputValue$.next("");
  }

  public loadAccounts(): void {
    if (this.isLoading) {
      this.cancelRequest();
      return;
    }

    this.isLoading = true;
    this.accountsRequest = this.accountService
      .getAllAccounts(this.searchInputValue, this.page)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: any) => {
        this.isLoading = false;
        this.page++;
        this.hasMoreData = res.paginationHasMoreData;

        this.accounts = [...this.accounts, ...res.data];
      });
  }

  openMenu(): void {
    this.isListOpen = !this.isListOpen;
    this.setStoredAccountAsRecent();
  }

  private addToRecents(account: any): void {
    this.recentAccounts = [account, ...this.recentAccounts]
      .filter(
        (value: any, index: number, arr: any) =>
          arr.map((obj: any) => obj.accountId).indexOf(value.accountId) ===
          index,
      )
      .slice(0, 5);

    AccountSelectComponent.storedRecentAccounts = this.recentAccounts;
  }

  private cancelRequest(): void {
    this.accountsRequest!.unsubscribe();
    this.isLoading = false;
    this.loadAccounts();
  }

  private getLoggedUser(): void {
    this.authService.userInfo$.pipe(take(1)).subscribe((userData) => {
      this.userId = userData.user.uuid || "";
    });
  }

  private setStoredAccountAsRecent(): void {
    if (this.userId) {
      const accountIdentifier = this.accountService.getStoredAccount(
        this.userId,
      );

      const initialAccount = this.accounts.find(
        (account) => account.externalIdentifier === accountIdentifier,
      );

      if (initialAccount) {
        this.addToRecents(initialAccount);
      }
    }
  }

  static set storedRecentAccounts(accounts: any) {
    CacheService.store(AccountSelectComponent.RECENT_ACCOUNTS, accounts);
  }

  static get storedRecentAccounts(): any {
    return CacheService.getCached<any[]>(
      AccountSelectComponent.RECENT_ACCOUNTS,
      [],
    );
  }

  get showRecents(): boolean {
    return !this.searchInputValue && !!this.recentAccounts.length;
  }
}
